// src/pages/AdminBlog.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Button, Card, CardContent,
    Grid, IconButton, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress, useTheme
} from '@mui/material';
import {
    Add, Edit, Delete, Visibility, VisibilityOff, KeyboardArrowLeft
} from '@mui/icons-material';
import AdminHeader from '../components/AdminHeader';
import blogApi from '../services/blogApi';

const AdminBlog = () => {
    const navigate = useNavigate();
    const theme = useTheme(); // ✅ useTheme
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, postId: null });

    useEffect(() => { fetchPosts(); }, []);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const data = await blogApi.getAllPosts();
            setPosts(data);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await blogApi.deletePost(deleteDialog.postId);
            setPosts(posts.filter(p => p.id !== deleteDialog.postId));
            setDeleteDialog({ open: false, postId: null });
        } catch (error) {
            console.error('Error deleting post:', error);
        }
    };

    const handleTogglePublish = async (id) => {
        try {
            const updatedPost = await blogApi.togglePublish(id);
            setPosts(posts.map(p => p.id === id ? updatedPost : p));
        } catch (error) {
            console.error('Error toggling publish:', error);
        }
    };

    // ===== LOADING =====
    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default, // ✅
                display: 'flex', justifyContent: 'center', alignItems: 'center'
            }}>
                <CircularProgress sx={{ color: '#ff7eb9' }} />
                <Typography variant="h6" sx={{ ml: 2, color: theme.palette.text.primary }}>
                    Вчитување...
                </Typography>
            </Box>
        );
    }

    return (
        // ✅ theme.palette.background.default
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="lg">
                <AdminHeader />

                {/* ✅ Nazad kopce */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        onClick={() => navigate('/admin')}
                        startIcon={<KeyboardArrowLeft sx={{ fontSize: '28px !important' }} />}
                        variant="outlined"
                        sx={{
                            color: '#ff7eb9', borderColor: '#ff7eb9',
                            backgroundColor: 'rgba(255,126,185,0.06)',
                            fontWeight: 'bold', fontSize: '1rem',
                            px: 3, py: 1.5, borderRadius: '12px',
                            textTransform: 'none',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#ff7eb9', color: 'white',
                                borderColor: '#ff4a97', transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(255,126,185,0.3)',
                            }
                        }}
                    >
                        Назад кон Admin Панел
                    </Button>
                </Box>

                {/* ===== HEADER ===== */}
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', mb: 6, flexWrap: 'wrap', gap: 2
                }}>
                    <Box>
                        <Typography variant="h3" sx={{
                            fontWeight: 900,
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '2rem', md: '2.5rem' }
                        }}>
                            📝 Блог Менаџмент
                        </Typography>
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                            {posts.length} статии вкупно
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/admin/blog/create')}
                        sx={{
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            color: 'white', fontWeight: 'bold',
                            px: 3, py: 1.5, borderRadius: '12px',
                            textTransform: 'none', fontSize: '1rem',
                            boxShadow: '0 4px 15px rgba(255,126,185,0.3)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #ff4a97, #9C27B0)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 25px rgba(255,126,185,0.4)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Додади Статија
                    </Button>
                </Box>

                {/* ===== NO POSTS ===== */}
                {posts.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 12,
                        border: '2px dashed rgba(255,126,185,0.3)',
                        borderRadius: '20px',
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255,126,185,0.05)'
                            : 'rgba(255,126,185,0.03)'
                    }}>
                        <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>📝</Typography>
                        <Typography variant="h5" sx={{
                            color: '#ff7eb9', mb: 1, fontWeight: 'bold'
                        }}>
                            Нема објавени статии
                        </Typography>
                        <Typography variant="body1" sx={{
                            color: theme.palette.text.secondary, mb: 4
                        }}>
                            Кликни на "Додади Статија" за да креираш нова
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate('/admin/blog/create')}
                            sx={{
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                color: 'white', fontWeight: 'bold',
                                px: 4, py: 1.5, borderRadius: '12px',
                                textTransform: 'none',
                                '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                            }}
                        >
                            Додади Статија
                        </Button>
                    </Box>
                ) : (
                    /* ===== POSTS GRID ===== */
                    <Grid container spacing={3}>
                        {posts.map((post) => (
                            <Grid item xs={12} md={6} key={post.id}>
                                <Card sx={{
                                    height: '100%',
                                    borderRadius: '20px',
                                    backgroundColor: theme.palette.background.paper, // ✅
                                    border: '1px solid rgba(255,126,185,0.15)',
                                    boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 16px 48px rgba(255,126,185,0.2)',
                                        borderColor: 'rgba(255,126,185,0.4)'
                                    }
                                }}>
                                    {/* Accent bar */}
                                    <Box sx={{
                                        height: 5,
                                        background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)'
                                    }} />

                                    {/* Post Image */}
                                    {post.imageUrl && (
                                        <Box sx={{
                                            height: 200,
                                            backgroundImage: `url(${post.imageUrl})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center'
                                        }} />
                                    )}

                                    <CardContent sx={{ p: 3 }}>
                                        {/* Title + Status */}
                                        <Box sx={{
                                            display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'flex-start', mb: 2, gap: 1
                                        }}>
                                            <Typography variant="h5" sx={{
                                                fontWeight: 'bold', flex: 1,
                                                color: theme.palette.text.primary // ✅
                                            }}>
                                                {post.title}
                                            </Typography>
                                            <Chip
                                                label={post.published ? 'Објавено' : 'Draft'}
                                                size="small"
                                                sx={post.published ? {
                                                    bgcolor: 'rgba(76,175,80,0.1)',
                                                    color: '#4CAF50',
                                                    fontWeight: 'bold',
                                                    border: '1px solid rgba(76,175,80,0.3)'
                                                } : {
                                                    bgcolor: theme.palette.mode === 'dark'
                                                        ? 'rgba(255,255,255,0.08)'
                                                        : 'rgba(0,0,0,0.06)',
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </Box>

                                        {/* Divider */}
                                        <Box sx={{
                                            width: 40, height: 3, borderRadius: 2,
                                            background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)',
                                            mb: 2
                                        }} />

                                        {/* Content preview */}
                                        <Typography variant="body2" sx={{
                                            mb: 2, color: theme.palette.text.secondary, // ✅
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden', lineHeight: 1.7
                                        }}>
                                            {post.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                        </Typography>

                                        {/* Date + Actions */}
                                        <Box sx={{
                                            display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'center', mt: 2,
                                            pt: 2, borderTop: `1px solid ${theme.palette.divider}`
                                        }}>
                                            <Typography variant="caption" sx={{
                                                color: theme.palette.text.secondary // ✅
                                            }}>
                                                📅 {new Date(post.createdAt).toLocaleDateString('mk-MK')}
                                            </Typography>

                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton size="small"
                                                            onClick={() => handleTogglePublish(post.id)}
                                                            title={post.published ? 'Скриј' : 'Објави'}
                                                            sx={{
                                                                color: post.published ? '#4CAF50' : theme.palette.text.disabled,
                                                                '&:hover': {
                                                                    bgcolor: post.published
                                                                        ? 'rgba(76,175,80,0.1)'
                                                                        : 'rgba(255,126,185,0.1)'
                                                                }
                                                            }}>
                                                    {post.published ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                                <IconButton size="small"
                                                            onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                                                            title="Уреди"
                                                            sx={{
                                                                color: '#a5d8ff',
                                                                '&:hover': { bgcolor: 'rgba(165,216,255,0.15)' }
                                                            }}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton size="small"
                                                            onClick={() => setDeleteDialog({ open: true, postId: post.id })}
                                                            title="Избриши"
                                                            sx={{
                                                                color: '#ff6b6b',
                                                                '&:hover': { bgcolor: 'rgba(255,107,107,0.1)' }
                                                            }}>
                                                    <Delete />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>

            {/* ===== DELETE DIALOG ===== */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, postId: null })}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        backgroundColor: theme.palette.background.paper, // ✅
                        border: '2px solid rgba(255,126,185,0.3)'
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                    color: 'white', fontWeight: 'bold', textAlign: 'center'
                }}>
                    ⚠️ Избриши статија
                </DialogTitle>
                <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.primary, mt: 1 }}>
                        Дали сте сигурни дека сакате да ја избришете оваа статија?
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                        Оваа акција е неповратна!
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, postId: null })}
                        variant="outlined"
                        sx={{
                            borderRadius: '10px', px: 3,
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.secondary,
                            '&:hover': { borderColor: '#ff7eb9', color: '#ff7eb9' }
                        }}
                    >
                        Откажи
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            borderRadius: '10px', px: 3, fontWeight: 'bold',
                            '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                        }}
                    >
                        Избриши
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminBlog;